import React from 'react';
import { motion } from 'framer-motion';
import { Zap, ShieldCheck, Move, RefreshCw, Calendar, Smartphone, PlayCircle, Activity, ArrowRight, CheckCircle2, Quote, Mail, Phone, Instagram, Globe, Users, Euro } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { IMAGES } from '@/assets/images';
import { BENEFICIOS_LIST, CARACTERISTICAS_PROGRAMA, TESTIMONIOS_LIST, scrollToAnchor } from '@/lib/index';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Checkbox } from '@/components/ui/checkbox';
const contactSchema = z.object({
  nombre: z.string().min(2, {
    message: 'El nombre es muy corto'
  }),
  email: z.string().email({
    message: 'Email inválido'
  }),
  mensaje: z.string().min(10, {
    message: 'Por favor, cuéntanos un poco más'
  }),
  privacidad: z.boolean().refine(val => val === true, {
    message: 'Debes aceptar la política de privacidad'
  })
});
type ContactFormValues = z.infer<typeof contactSchema>;
const IconMap: Record<string, React.ReactNode> = {
  Zap: <Zap className="w-6 h-6" />,
  ShieldCheck: <ShieldCheck className="w-6 h-6" />,
  Move: <Move className="w-6 h-6" />,
  RefreshCw: <RefreshCw className="w-6 h-6" />,
  Calendar: <Calendar className="w-6 h-6" />,
  Smartphone: <Smartphone className="w-6 h-6" />,
  PlayCircle: <PlayCircle className="w-6 h-6" />,
  Activity: <Activity className="w-6 h-6" />,
  Users: <Users className="w-6 h-6" />,
  Euro: <Euro className="w-6 h-6" />
};
export default function Home() {
  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      nombre: '',
      email: '',
      mensaje: ''
    }
  });
  const onSubmit = (data: ContactFormValues) => {
    console.log('Form submitted:', data);
    alert('¡Gracias! Nos pondremos en contacto contigo pronto.');
    form.reset();
  };
  return (
    <div className="flex flex-col w-full">
      {/* Hero Section */}
      <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={IMAGES.IMG_7425147165113839616_42} alt="Programa Recupera - Fondo" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/70" />
        </div>

        <div className="container mx-auto px-4 relative z-10 text-center max-w-4xl">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          ease: [0.16, 1, 0.3, 1]
        }}>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
              Recupera tu <span className="text-primary">Energía</span> y vive sin molestias
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground mb-10 max-w-2xl mx-auto">
              Un programa integral de 12 meses diseñado para transformar tu salud física a través del entrenamiento, la respiración y hábitos sostenibles.
            </p>
            <div className="flex justify-center">
              <Button size="lg" className="text-lg h-14 px-8 rounded-full bg-primary hover:bg-primary/90 transition-all" onClick={() => window.open('https://8798b9-2.myshopify.com/cart/52472610423125:1?channel=buy_button', '_blank')}>
                Solo 10€/mes* <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </motion.div>
        </div>

        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce opacity-50">
          <div className="w-px h-12 bg-foreground/20" />
        </div>
      </section>

      {/* ¿Qué es el Programa Recupera? Section */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }}>
              <h2 className="text-4xl md:text-5xl font-bold mb-8">
                ¿Qué es el <span className="text-primary">Programa Recupera</span>?
              </h2>
              <div className="text-lg md:text-xl text-muted-foreground mb-12 leading-relaxed space-y-6">
                <p>
                  Es un programa integral diseñado específicamente para personas que sufren dolor crónico, 
                  molestias diarias y falta de energía. A través de una metodología probada que combina 
                  <strong className="text-foreground"> entrenamiento físico progresivo</strong>, 
                  <strong className="text-foreground"> técnicas de respiración</strong> y 
                  <strong className="text-foreground"> formación de hábitos saludables</strong>.
                </p>
                <p>
                  No es solo otro programa de ejercicios. Es una transformación completa de tu relación 
                  con tu cuerpo, diseñada para que recuperes tu vitalidad y vivas sin las limitaciones 
                  que el dolor te ha impuesto.
                </p>
                <p className="text-primary font-semibold">12 meses de programa para mejorar tu día a día para siempre.</p>
              </div>
            </motion.div>
            
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} className="mt-16">
              <img src="https://static-us-img.skywork.ai/prod/user/head_picture/2019412976355164160_que es el programa.png?image_process=quality,q_90/resize,w_1280/format,webp" alt="Programa Recupera - Metodología Integral" className="w-full h-auto object-contain" />
              
              {/* Elementos decorativos */}
              <div className="absolute -top-8 -left-8 w-32 h-32 bg-primary/5 rounded-full blur-2xl" />
              <div className="absolute -bottom-8 -right-8 w-24 h-24 bg-primary/10 rounded-full blur-xl" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Programa Section */}
      <section id="programa" className="py-24 bg-card/50">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }}>
              <h2 className="text-4xl font-bold mb-6">¿Qué Beneficios tiene el Programa?</h2>
              <p className="text-lg text-muted-foreground mb-8 leading-relaxed">Programa Recupera no solo alivia y elimina tus molestias, sino que te devuelve la vitalidad y confianza para vivir plenamente. Estos son los cambios que experimentarás:</p>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                    <CheckCircle2 className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="text-xl font-semibold mb-2">Reducción del dolor crónico</h4>
                    <p className="text-muted-foreground">Elimina las molestias diarias como el dolor lumbar, caderas, cervical y espalda en general. </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                    <CheckCircle2 className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="text-xl font-semibold mb-2">Aumento de energía diaria</h4>
                    <p className="text-muted-foreground">Recupera la vitalidad que tenías antes, despiértate con ganas y mantén la energía todo el día.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                    <CheckCircle2 className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="text-xl font-semibold mb-2">Mejor calidad de sueño</h4>
                    <p className="text-muted-foreground">Duerme profundamente y despierta descansado gracias a las técnicas de respiración y relajación.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  
                  <div>
                    
                    
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="relative">
              <img src="https://static-us-img.skywork.ai/prod/user/head_picture/2019409964089917440_WhatsApp Image 2026-02-05 at 13.18.18.jpeg?image_process=quality,q_90/resize,w_1280/format,webp" alt="Persona con energía y vitalidad" className="rounded-2xl shadow-2xl h-[333.265625px] object-cover" />
              
            </motion.div>
          </div>
        </div>
      </section>

      {/* Tres Pilares Section */}
      <section className="py-32 bg-muted/20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Los <span className="text-primary">Tres Pilares</span> del Programa
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Nuestro programa se basa en una metodología integral que combina tres elementos fundamentales 
              para tu recuperación y bienestar completo.
            </p>
          </div>

          <div className="space-y-24 max-w-6xl mx-auto">
            {/* Pilar 1: Ejercicio */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.1
          }} className="grid lg:grid-cols-2 gap-16 items-center group">
              <div className="relative">
                <img src={IMAGES.EJERCICIO} alt="Ejercicio - Primer Pilar" className="w-full h-auto object-contain group-hover:scale-105 transition-transform duration-300" />
                <div className="absolute -top-4 -left-4 w-16 h-16 bg-primary rounded-full flex items-center justify-center shadow-lg">
                  <span className="text-white font-mono text-2xl font-bold">01</span>
                </div>
              </div>
              <div className="space-y-6">
                <h3 className="text-3xl font-bold text-primary">Ejercicio</h3>
                <p className="text-lg text-muted-foreground leading-relaxed">Rutinas de entrenamiento progresivas diseñadas para  mejorar tu movilidad, ganar fuerza y reducir el dolor de forma gradual y sostenible. Cada ejercicio está explicado paso a paso con videos HD y subtítulos. </p>
                <ul className="space-y-3 text-muted-foreground">
                  <li className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-primary rounded-full"></div>
                    Rutinas progresivas de movilidad fuerza y cardio
                  </li>
                  <li className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-primary rounded-full"></div>
                    Videos explicativos paso a paso
                  </li>
                  <li className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-primary rounded-full"></div>
                    Opciones alternativas en la mayoría de ejercicios
                  </li>
                </ul>
              </div>
            </motion.div>

            {/* Pilar 2: Respiración */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="grid lg:grid-cols-2 gap-16 items-center group">
              <div className="lg:order-2 relative">
                <img src={IMAGES.RESPIRACION} alt="Respiración - Segundo Pilar" className="w-full h-auto object-contain group-hover:scale-105 transition-transform duration-300" />
                <div className="absolute -top-4 -right-4 w-16 h-16 bg-primary rounded-full flex items-center justify-center shadow-lg">
                  <span className="text-white font-mono text-2xl font-bold">02</span>
                </div>
              </div>
              <div className="lg:order-1 space-y-6">
                <h3 className="text-3xl font-bold text-primary">Respiración</h3>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  Técnicas de respiración consciente que te ayudarán a reducir el estrés, 
                  mejorar la calidad del sueño y potenciar la recuperación de tu cuerpo. 
                  Aprende a usar tu respiración como herramienta de sanación.
                </p>
                <ul className="space-y-3 text-muted-foreground">
                  <li className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-primary rounded-full"></div>
                    Técnicas de respiración consciente
                  </li>
                  <li className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-primary rounded-full"></div>
                    Reducción de estrés y ansiedad
                  </li>
                  <li className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-primary rounded-full"></div>
                    Mejora de la calidad del sueño
                  </li>
                </ul>
              </div>
            </motion.div>

            {/* Pilar 3: Hábitos */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.3
          }} className="grid lg:grid-cols-2 gap-16 items-center group">
              <div className="relative">
                <img src={IMAGES.HABITOS} alt="Hábitos - Tercer Pilar" className="w-full h-auto object-contain group-hover:scale-105 transition-transform duration-300" />
                <div className="absolute -top-4 -left-4 w-16 h-16 bg-primary rounded-full flex items-center justify-center shadow-lg">
                  <span className="text-white font-mono text-2xl font-bold">03</span>
                </div>
              </div>
              <div className="space-y-6">
                <h3 className="text-3xl font-bold text-primary">Hábitos</h3>
                <p className="text-lg text-muted-foreground leading-relaxed">Construye rutinas diarias saludables que transformen tu estilo de vida. Desde el poder del 1% hasta la mejora del descanso, te guiamos para crear hábitos que mantengan tu bienestar a largo plazo.</p>
                <ul className="space-y-3 text-muted-foreground">
                  <li className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-primary rounded-full"></div>
                    Comprende la dopamina y aplica el poder del 1%
                  </li>
                  <li className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-primary rounded-full"></div>
                    Equilibra tu sistema nervioso autónomo
                  </li>
                  <li className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-primary rounded-full"></div>
                    Mejora tus ritmos circadianos
                  </li>
                </ul>
              </div>
            </motion.div>
          </div>

          <div className="text-center mt-16">
            <p className="text-lg text-muted-foreground mb-8">
              Estos tres pilares trabajan en sinergia para ofrecerte una transformación completa y duradera.
            </p>
            <Button size="lg" className="rounded-full px-8 h-12 bg-primary hover:bg-primary/90" onClick={() => window.open('https://8798b9-2.myshopify.com/cart/52472610423125:1?channel=buy_button', '_blank')}>
              Solo 10€/mes* <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* Caminas en Compania Section */}
      <section className="py-24 bg-muted/10">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Caminas en <span className="text-primary">Compania</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              No estaras solo con una programacion dentro de una app. Tendras acceso a una comunidad privada donde podras preguntar dudas, compartir experiencias y hasta enviar videos de tus ejercicios para que sean corregidos.
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Pregunta tus Dudas</h3>
                <p className="text-muted-foreground">
                  Resuelve cualquier cuestion sobre ejercicios, tecnicas de respiracion o habitos.
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Comparte Experiencias</h3>
                <p className="text-muted-foreground">
                  Conecta con personas que estan viviendo el mismo proceso de transformacion.
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Envia Videos</h3>
                <p className="text-muted-foreground">
                  Recibe feedback personalizado enviando videos de tus ejercicios.
                </p>
              </div>
            </div>
            
            <div className="text-center mt-12">
              <img
                src={IMAGES.COMUNICACION_SOPORTE_GRAFICO}
                alt="Comunicacion y soporte comunitario"
                className="w-full max-w-2xl mx-auto rounded-2xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Producto Digital Section */}
      
      {/* Beneficios Section */}
      <section id="beneficios" className="py-24">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">¿Por qué elegir Programa Recupera?</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Combinamos ciencia deportiva, fisioterapia y técnicas ancestrales de respiración en una sola herramienta.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {BENEFICIOS_LIST.map((beneficio, index) => <motion.div key={beneficio.id} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }}>
                <Card className="h-full border-none bg-muted/30 hover:bg-muted/50 transition-colors duration-300">
                  <CardContent className="pt-8">
                    <div className="mb-4 w-12 h-12 rounded-lg bg-primary flex items-center justify-center text-white shadow-lg">
                      {IconMap[beneficio.icono]}
                    </div>
                    <h3 className="text-xl font-semibold mb-3">{beneficio.titulo}</h3>
                    <p className="text-muted-foreground">{beneficio.descripcion}</p>
                  </CardContent>
                </Card>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Caracteristicas / App Section */}
      

      {/* Testimonios Section */}
      <section id="testimonios" className="py-24 bg-muted/10">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Lo que dicen nuestros <span className="text-primary">usuarios</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Testimonios reales de personas que han transformado su vida con el Programa Recupera
            </p>
          </div>

          {/* Testimonios en móviles solapados horizontalmente */}
          <div className="relative max-w-7xl mx-auto">
            <div className="flex justify-center items-center overflow-hidden">
              {/* Testimonio 1 */}
              <motion.div initial={{
              opacity: 0,
              x: -50
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.1
            }} className="relative z-10 transform -rotate-3 hover:rotate-0 transition-transform duration-300">
                <img src={IMAGES.TESTIMONIO1} alt="Testimonio 1" className="w-64 h-auto rounded-2xl shadow-2xl border-4 border-white" />
              </motion.div>

              {/* Testimonio 2 */}
              <motion.div initial={{
              opacity: 0,
              x: -30
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.2
            }} className="relative z-20 transform rotate-2 hover:rotate-0 transition-transform duration-300 -ml-8">
                <img src={IMAGES.TESTIMONIO2} alt="Testimonio 2" className="w-64 h-auto rounded-2xl shadow-2xl border-4 border-white" />
              </motion.div>

              {/* Testimonio 3 */}
              <motion.div initial={{
              opacity: 0,
              y: -30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.3
            }} className="relative z-30 transform -rotate-1 hover:rotate-0 transition-transform duration-300 -ml-8">
                <img src={IMAGES.TESTIMONIO3} alt="Testimonio 3" className="w-64 h-auto rounded-2xl shadow-2xl border-4 border-white" />
              </motion.div>

              {/* Testimonio 4 */}
              <motion.div initial={{
              opacity: 0,
              x: 30
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.4
            }} className="relative z-20 transform rotate-3 hover:rotate-0 transition-transform duration-300 -ml-8">
                <img src={IMAGES.TESTIMONIO4} alt="Testimonio 4" className="w-64 h-auto rounded-2xl shadow-2xl border-4 border-white" />
              </motion.div>

              {/* Testimonio 5 */}
              <motion.div initial={{
              opacity: 0,
              x: 50
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.5
            }} className="relative z-10 transform -rotate-2 hover:rotate-0 transition-transform duration-300 -ml-8">
                <img src={IMAGES.TESTIMONIO5} alt="Testimonio 5" className="w-64 h-auto rounded-2xl shadow-2xl border-4 border-white" />
              </motion.div>
            </div>

            {/* Segunda fila de testimonios (móvil) */}
            <div className="flex justify-center items-center overflow-hidden mt-8">
              {/* Testimonio 6 */}
              <motion.div initial={{
              opacity: 0,
              x: -40
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.6
            }} className="relative z-15 transform rotate-1 hover:rotate-0 transition-transform duration-300">
                <img src={IMAGES.TESTIMONIO6} alt="Testimonio 6" className="w-64 h-auto rounded-2xl shadow-2xl border-4 border-white" />
              </motion.div>

              {/* Testimonio 7 */}
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.7
            }} className="relative z-25 transform -rotate-2 hover:rotate-0 transition-transform duration-300 -ml-8">
                <img src={IMAGES.TESTIMONIO7} alt="Testimonio 7" className="w-64 h-auto rounded-2xl shadow-2xl border-4 border-white" />
              </motion.div>

              {/* Testimonio 8 */}
              <motion.div initial={{
              opacity: 0,
              x: 20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.8
            }} className="relative z-15 transform rotate-2 hover:rotate-0 transition-transform duration-300 -ml-8">
                <img src={IMAGES.TESTIMONIO8} alt="Testimonio 8" className="w-64 h-auto rounded-2xl shadow-2xl border-4 border-white" />
              </motion.div>

              {/* Testimonio 9 */}
              <motion.div initial={{
              opacity: 0,
              x: 40
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.9
            }} className="relative z-10 transform -rotate-1 hover:rotate-0 transition-transform duration-300 -ml-8">
                <img src={IMAGES.TESTIMONIO9} alt="Testimonio 9" className="w-64 h-auto rounded-2xl shadow-2xl border-4 border-white" />
              </motion.div>
            </div>
          </div>

          {/* CTA después de testimonios */}
          <div className="text-center mt-16">
            <p className="text-lg text-muted-foreground mb-8">
              Únete a miles de personas que ya han transformado su vida
            </p>
            <Button size="lg" className="rounded-full px-8 h-12 bg-primary hover:bg-primary/90" onClick={() => window.open('https://8798b9-2.myshopify.com/cart/52472610423125:1?channel=buy_button', '_blank')}>
              Solo 10€/mes* <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* Preguntas Frecuentes Section */}
      <section className="py-24 bg-muted/10">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Preguntas <span className="text-primary">Frecuentes</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Resolvemos las dudas más comunes sobre el Programa Recupera
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="space-y-6">
              {/* FAQ 1 */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.1
            }} className="bg-background rounded-2xl p-6 shadow-lg border border-border/20">
                <h3 className="text-xl font-semibold mb-3 text-primary">
                  ¿Cuánto tiempo dura el programa?
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  El Programa Recupera tiene una duración de 12 meses, dividido en 5 fases. Cada fase se centra en aspectos clave para que el programa sea progresivo y accesible.
                </p>
              </motion.div>

              {/* FAQ 2 */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.2
            }} className="bg-background rounded-2xl p-6 shadow-lg border border-border/20">
                <h3 className="text-xl font-semibold mb-3 text-primary">
                  ¿Necesito experiencia previa en ejercicio?
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  No es necesario tener experiencia previa. El programa está diseñado para adaptarse a todos los niveles, desde principiantes hasta personas con experiencia. Los ejercicios son progresivos y se ajustan a tu ritmo.
                </p>
              </motion.div>

              {/* FAQ 3 */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.3
            }} className="bg-background rounded-2xl p-6 shadow-lg border border-border/20">
                <h3 className="text-xl font-semibold mb-3 text-primary">
                  ¿Cómo accedo al contenido del programa?
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  Todo el contenido está disponible a través de nuestra aplicación móvil. Tendrás acceso a videos explicativos, rutinas de ejercicio, técnicas de respiración y seguimiento de hábitos, todo desde tu teléfono.
                </p>
              </motion.div>

              {/* FAQ 4 */}
              

              {/* FAQ 5 */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.5
            }} className="bg-background rounded-2xl p-6 shadow-lg border border-border/20">
                <h3 className="text-xl font-semibold mb-3 text-primary">
                  ¿Puedo hacer el programa si tengo lesiones?
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  El programa está especialmente diseñado para personas con molestias y dolor. Sin embargo, recomendamos consultar con tu médico antes de comenzar cualquier programa de ejercicios, especialmente si tienes lesiones específicas.
                </p>
              </motion.div>

              {/* FAQ 6 */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.6
            }} className="bg-background rounded-2xl p-6 shadow-lg border border-border/20">
                <h3 className="text-xl font-semibold mb-3 text-primary">
                  ¿Hay soporte durante el programa?
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  Sí, ofrecemos soporte continuo a través de la comunidad privada. Puedes hacer preguntas, recibir consejos personalizados y conectar con otros participantes del programa para mantenerte motivado.
                </p>
              </motion.div>

              {/* FAQ 7 */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.7
            }} className="bg-background rounded-2xl p-6 shadow-lg border border-border/20">
                <h3 className="text-xl font-semibold mb-3 text-primary">
                  ¿Para quién es el programa?
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  El programa está diseñado para personas que sufren dolor crónico, molestias musculares, falta de energía o problemas de movilidad. Es ideal para adultos de cualquier edad que buscan recuperar su bienestar físico y mental de forma natural y sostenible.
                </p>
              </motion.div>

              {/* FAQ 8 */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.8
            }} className="bg-background rounded-2xl p-6 shadow-lg border border-border/20">
                <h3 className="text-xl font-semibold mb-3 text-primary">
                  ¿Puede hacerlo en casa?
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  El primer mes se puede realizar completamente en casa sin problema, utilizando un roller, unas cuñas de sentadilla, tacos de yoga y una banda elástica. Para los siguientes meses, lo ideal es acceder a un gimnasio básico, aunque también es posible continuar en casa adquiriendo algunas mancuernas o kettlebells para maximizar los resultados.
                </p>
              </motion.div>

              {/* FAQ 9 */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.9
            }} className="bg-background rounded-2xl p-6 shadow-lg border border-border/20">
                <h3 className="text-xl font-semibold mb-3 text-primary">
                  ¿Qué material básico necesito?
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  Material básico necesario: roller, cuñas de sentadilla y tacos de yoga. Si decides no realizar el programa en un gimnasio, es recomendable hacerse con alguna mancuerna o kettlebell y banda elástica para obtener los mejores resultados en las fases más avanzadas.
                </p>
              </motion.div>

              {/* FAQ 10 */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 1.0
            }} className="bg-background rounded-2xl p-6 shadow-lg border border-border/20">
                <h3 className="text-xl font-semibold mb-3 text-primary">
                  ¿Y si ya estoy entrenando?
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  Perfecto. Si ya estás entrenando, puedes usar las rutinas de los primeros meses como calentamientos y/o píldoras de movimiento para casa. Según avance la planificación, puedes ir incorporando las propuestas del programa poco a poco en tu rutina actual hasta generar una rutina "híbrida" entre el programa y tu entrenamiento actual.
                </p>
              </motion.div>

              {/* FAQ 11 */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 1.1
            }} className="bg-background rounded-2xl p-6 shadow-lg border border-border/20">
                <h3 className="text-xl font-semibold mb-3 text-primary">
                  ¿Qué pasa al terminar los 12 meses?
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  Al completar el programa, habrás desarrollado hábitos sólidos y conocimientos para mantener tu bienestar de forma autónoma. Puedes continuar en la app y comunidad por el mismo precio (10€/mes) con acceso a rutinas de entrenamiento que se actualizarán de forma periódica, manteniendo tu progreso y evolución constante.
                </p>
              </motion.div>

              {/* FAQ 12 */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 1.2
            }} className="bg-background rounded-2xl p-6 shadow-lg border border-border/20">
                <h3 className="text-xl font-semibold mb-3 text-primary">
                  ¿Se me cobra algo ahora?
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  Con el pago inicial de 47€ se incluye el acceso a la app y primer mes de planificación. Una vez que activas tu cuenta en la app, la suscripción no se activará hasta pasados 30 días de la activación de la app. Recuerda que el primer mes está incluido con el pdf. No se te hará ningún cobro hasta pasado ese tiempo. Puedes darte de baja de la suscripción fácilmente desde tu perfil dentro de la app.
                </p>
              </motion.div>
            </div>

            {/* CTA al final de FAQ */}
            
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Inversión <span className="text-primary">Transparente</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Sin sorpresas, sin costos ocultos. Conoce exactamente lo que inviertes en tu transformación
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-3xl p-8 md:p-12 border border-primary/20">
              <div className="text-center mb-8">
                <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-semibold mb-4">
                  💰 Oferta de Lanzamiento
                </div>
                <h3 className="text-3xl md:text-4xl font-bold mb-4">
                  Solo <span className="text-primary">10€ al mes</span>
                </h3>
                <p className="text-lg text-muted-foreground">
                  Después del primer mes
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-white font-bold text-sm">
                      1
                    </div>
                    <div>
                      <h4 className="text-xl font-semibold mb-2">Primer Mes: 47€</h4>
                      <p className="text-muted-foreground">Incluye todos los documentos del programa y el primer mes de suscripción de regalo. Acceso completo a la app y contenido comunidad privada</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-white font-bold text-sm">
                      2
                    </div>
                    <div>
                      <h4 className="text-xl font-semibold mb-2">Siguientes 11 Meses: 10€/mes</h4>
                      <p className="text-muted-foreground">Acceso continuo a la app, actualizaciones de contenido, soporte personalizado y seguimiento de progreso en la comunidad.</p>
                    </div>
                  </div>

                  <div className="bg-white/50 rounded-2xl p-4 border border-primary/20">
                    <p className="text-sm text-muted-foreground text-center">
                      <strong>*Primer mes:</strong> 47€ (incluye documentos + primer mes gratis)
                    </p>
                  </div>
                </div>

                <div className="text-center">
                  <div className="bg-white rounded-2xl p-8 shadow-lg border border-border/20">
                    <h4 className="text-2xl font-bold mb-4">Lo que obtienes:</h4>
                    <ul className="space-y-3 text-left">
                      <li className="flex items-center gap-3">
                        <CheckCircle2 className="w-5 h-5 text-primary" />
                        <span>Programa completo de 12 meses</span>
                      </li>
                      <li className="flex items-center gap-3">
                        <CheckCircle2 className="w-5 h-5 text-primary" />
                        <span>App móvil con videos HD</span>
                      </li>
                      <li className="flex items-center gap-3">
                        <CheckCircle2 className="w-5 h-5 text-primary" />
                        <span>Documentos y guías PDF</span>
                      </li>
                      <li className="flex items-center gap-3">
                        <CheckCircle2 className="w-5 h-5 text-primary" />
                        <span>Soporte en comunidad privada</span>
                      </li>
                      <li className="flex items-center gap-3">
                        <CheckCircle2 className="w-5 h-5 text-primary" />
                        <span>Seguimiento de progreso</span>
                      </li>
                      <li className="flex items-center gap-3">
                        <CheckCircle2 className="w-5 h-5 text-primary" />
                        <span>Actualizaciones gratuitas</span>
                      </li>
                      <li className="flex items-center gap-3">
                        <CheckCircle2 className="w-5 h-5 text-primary" />
                        <span>Una vida sin dolor y con energía</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="text-center mt-8">
                <Button size="lg" className="rounded-full px-12 h-14 bg-primary text-xl hover:bg-primary/90" onClick={() => window.open('https://8798b9-2.myshopify.com/cart/52472610423125:1?channel=buy_button', '_blank')}>
                  Solo 10€/mes* <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <p className="text-sm text-muted-foreground mt-3">
                  *Primer mes 47€ • Cancela cuando quieras sin ningún problema
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA / Contacto Section */}
      <section id="contacto" className="py-24 bg-muted/20">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto bg-background rounded-3xl overflow-hidden shadow-2xl border border-border/40">
            <div className="grid md:grid-cols-2">
              <div className="p-8 md:p-12 bg-primary text-white">
                <h2 className="text-4xl font-bold mb-6">¿Tienes alguna duda?</h2>
                <p className="text-white/80 mb-10 text-lg">
                  Si tienes dudas o quieres saber si este programa es para ti, déjanos un mensaje y te ayudaremos a decidir.
                </p>
                <div className="space-y-6">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                      <Mail className="w-5 h-5" />
                    </div>
                    <span className="text-lg">soporte@gamantrainin.com</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                      <Globe className="w-5 h-5" />
                    </div>
                    <a href="https://www.gamantraining.com" target="_blank" rel="noopener noreferrer" className="text-lg hover:text-white/80 transition-colors">
                      www.gamantraining.com
                    </a>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                      <Instagram className="w-5 h-5" />
                    </div>
                    <span className="text-lg">@gamantraining</span>
                  </div>
                </div>
              </div>

              <div className="p-8 md:p-12">
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                    <FormField control={form.control} name="nombre" render={({
                    field
                  }) => <FormItem>
                        <FormLabel>Nombre completo</FormLabel>
                        <FormControl>
                          <Input placeholder="Tu nombre" {...field} className="h-12" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />
                    <FormField control={form.control} name="email" render={({
                    field
                  }) => <FormItem>
                        <FormLabel>Correo electrónico</FormLabel>
                        <FormControl>
                          <Input placeholder="tu@email.com" {...field} className="h-12" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />
                    <FormField control={form.control} name="mensaje" render={({
                    field
                  }) => <FormItem>
                        <FormLabel>¿Cómo podemos ayudarte?</FormLabel>
                        <FormControl>
                          <Textarea placeholder="Cuéntanos tu situación actual..." className="min-h-[120px] resize-none" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />
                    
                    <FormField control={form.control} name="privacidad" render={({
                    field
                  }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                        <FormControl>
                          <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel className="text-sm">
                            Acepto la{" "}
                            <a href="https://www.gamantraining.com/politica-privacidad" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                              política de privacidad
                            </a>
                            {" "}y el tratamiento de mis datos personales.
                          </FormLabel>
                          <FormMessage />
                        </div>
                      </FormItem>} />
                    
                    <Button type="submit" className="w-full h-12 text-lg bg-primary hover:bg-primary/90">
                      Enviar Mensaje
                    </Button>
                  </form>
                </Form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer Final CTA Background */}
      <section className="py-20 bg-background overflow-hidden relative">
        <div className="container mx-auto px-4 text-center">
          <img src={IMAGES.BREATHING_WELLNESS_1} alt="Calma y bienestar" className="absolute inset-0 w-full h-full object-cover opacity-10 pointer-events-none" />
          <div className="relative z-10">
            <h2 className="text-3xl md:text-5xl font-bold mb-8">Transforma tu vida, un día a la vez.</h2>
            <Button size="lg" className="rounded-full px-12 h-14 bg-primary text-xl" onClick={() => window.open('https://8798b9-2.myshopify.com/cart/52472610423125:1?channel=buy_button', '_blank')}>
              Solo 10€/mes*
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}